import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { CFOP_OPTIONS } from './SelectCFOP';

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  size?: 'sm' | 'lg';
  onBlur?: () => void;
}

const CfopInput = React.forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, onBlur, disabled, isInvalid, size }, ref) => {
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOption = CFOP_OPTIONS.find(o => o.value === value);

    const filtered = query.trim()
      ? CFOP_OPTIONS.filter(
          o => o.value.startsWith(query) || o.label.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 12)
      : [];

    const handleFocus = () => {
      setFocused(true);
      setQuery(value);
      if (value) {
        const initial = CFOP_OPTIONS.filter(o => o.value.startsWith(value)).slice(0, 12);
        setOpen(initial.length > 0);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setQuery(v);
      if (v.trim()) {
        const opts = CFOP_OPTIONS.filter(
          o => o.value.startsWith(v) || o.label.toLowerCase().includes(v.toLowerCase())
        ).slice(0, 12);
        setOpen(opts.length > 0);
      } else {
        setOpen(false);
      }
    };

    const handleSelect = (option: (typeof CFOP_OPTIONS)[0]) => {
      onChange(option.value);
      setFocused(false);
      setOpen(false);
      setQuery('');
    };

    const handleBlur = () => {
      setTimeout(() => {
        setFocused(false);
        setOpen(false);
        setQuery('');
        onBlur?.();
      }, 150);
    };

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) {
          setFocused(false);
          setOpen(false);
          setQuery('');
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    const inputValue = focused
      ? query
      : selectedOption
        ? `${selectedOption.value} — ${selectedOption.label}`
        : value || '';

    return (
      <div ref={containerRef} style={{ position: 'relative' }}>
        <Form.Control
          ref={ref}
          size={size}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          isInvalid={isInvalid}
          placeholder="CFOP..."
          autoComplete="off"
        />
        {open && focused && filtered.length > 0 && (
          <div className="nfe-natureza-suggestions nfe-cfop-suggestions">
            {filtered.map(o => (
              <div
                key={o.value}
                className="nfe-natureza-suggestion-item nfe-cfop-suggestion-item"
                onMouseDown={() => handleSelect(o)}
              >
                <span className="nfe-natureza-cfop-code">{o.value}</span>
                <span className="nfe-natureza-cfop-label" style={{ whiteSpace: 'normal' }}>{o.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

CfopInput.displayName = 'CfopInput';
export default CfopInput;
